import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ClassificationItemsSection.scss';
import {
  BdsTooltip,
  BdsButton,
  BdsChipClickable,
} from '@/core/constants/webComponents';
import { Post } from '@/core/services/interfaces/PostInterfaces';
import selectorService from '@/core/services/SelectorService';
import { ClassificationConfirmDialog } from './ClassificationConfirmDialog';
import { ModalSearchableDropdown } from './ModalSearchableDropdown';
import { normalizeString as normalize } from '@/core/utils/normalizeString';
import './ModalSearchableDropdown.scss';

interface ClassificationItemsSectionProps {
  post: Post;
  onDispatch: (eventName: string, extra?: Record<string, unknown>) => void;
  CLASSIFICATION_EVENTS: any;
  availableGroups: string[];
  availableThemes: string[];
  availableInterests: string[];
  availableTags: string[];
  loadingAvailableItems: boolean;
  onAddTagsLocally?: (tags: string[]) => void;
  onRefreshTags?: () => Promise<void>;
}

type ItemType = 'group' | 'theme' | 'interest' | 'tag';

export const INITIAL_VISIBLE_CHIPS = 100;
export const CHIPS_EXPANSION_STEP = 100;

function applySelectionMap(prev: string[], selectionMap: Record<string, 'add' | 'remove'>): string[] {
  return Object.entries(selectionMap).reduce((updated, [name, state]) => {
    if (state === 'add' && !updated.includes(name)) return [...updated, name];
    if (state === 'remove') return updated.filter((n) => n !== name);
    return updated;
  }, prev);
}

const AVG_CHAR_WIDTH = 7.5;
const CHIP_UI_OFFSET = 48;
const CHIP_MIN_WIDTH = 40;
const ELLIPSIS = '…';

function truncateResponsive(text: string, containerWidth: number): { text: string; truncated: boolean } {
  if (containerWidth <= 0) return { text, truncated: false };

  const availableWidth = Math.max(containerWidth - CHIP_UI_OFFSET, CHIP_MIN_WIDTH);
  const maxChars = Math.floor(availableWidth / AVG_CHAR_WIDTH);

  if (text.length <= maxChars) {
    return { text, truncated: false };
  }

  return { 
    text: text.slice(0, Math.max(0, maxChars - 1)) + ELLIPSIS, 
    truncated: true 
  };
}

function ChipGroup({
  items,
  onChipClose,
}: {
  readonly items: readonly string[];
  readonly onChipClose: (item: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Map<number, HTMLElement>>(new Map());
  const [containerWidth, setContainerWidth] = useState(0);

  const setChipRef = useCallback((index: number, el: HTMLElement | null) => {
    if (el) {
      chipRefs.current.set(index, el);
    } else {
      chipRefs.current.delete(index);
    }
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const width = Math.round(entries[0].contentRect.width);
      setContainerWidth((prev) => (prev === width ? prev : width));
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const displayItems = useMemo(
    () => items.map((item) => truncateResponsive(item, containerWidth)),
    [items, containerWidth],
  );

  useEffect(() => {
    const cleanups: (() => void)[] = [];

    chipRefs.current.forEach((el, index) => {
      if (index >= items.length) return;
      const item = items[index];
      const handler = () => onChipClose(item);
      el.addEventListener('chipClickableClose', handler);
      cleanups.push(() => el.removeEventListener('chipClickableClose', handler));
    });

    return () => cleanups.forEach((fn) => fn());
  });

  return (
    <div ref={containerRef} data-testid="chip-group-wrapper" className="classification-card__chip-group">
      {items.map((item, index) => {
        const { text, truncated } = displayItems[index];

        const chip = (
          <BdsChipClickable
            ref={(el: HTMLElement | null) => setChipRef(index, el)}
            close
            class="classification-card__chip"
            color="outline"
          >
            <span className="classification-card__chip-text">
              {text}
            </span>
          </BdsChipClickable>
        );

        if (truncated) {
          return (
            <BdsTooltip key={item} position="top-center" tooltip-text={item}>
              {chip}
            </BdsTooltip>
          );
        }

        return <React.Fragment key={item}>{chip}</React.Fragment>;
      })}
    </div>
  );
}

interface InlineDropdownProps {
  readonly availableItems: readonly string[];
  readonly onAdd: (item: string) => void;
  readonly onClose: () => void;
  readonly isTag?: boolean;
  readonly onCreateTag?: (name: string) => void;
  readonly loading?: boolean;
}

function InlineDropdown({ availableItems, onAdd, onClose, isTag, onCreateTag, loading }: InlineDropdownProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = normalize(search.trim());
    return availableItems.filter((item) => !q || normalize(item).includes(q));
  }, [availableItems, search]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target;
      const targetElement = target instanceof Element ? target : null;

      if (targetElement?.closest('[data-testid="confirm-dialog"], .classification-confirm-dialog')) {
        return;
      }
      if (targetElement?.closest('[data-classification-add-toggle]')) {
        return;
      }
      if (containerRef.current && target && !containerRef.current.contains(target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [onClose]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const preventFocusLoss = (e: MouseEvent) => {
      const target = e.target;
      if (target instanceof Element && target.closest('input')) {
        return;
      }
      e.preventDefault();
    };

    el.addEventListener('mousedown', preventFocusLoss);
    return () => el.removeEventListener('mousedown', preventFocusLoss);
  }, []);

  const handleAddClick = useCallback((item: string) => {
    onAdd(item);
    setSearch('');
  }, [onAdd]);

  const handleCreateTag = useCallback((name: string) => {
    if (onCreateTag) {
      onCreateTag(name);
    }
    setSearch('');
  }, [onCreateTag]);

  return (
    <div
      ref={containerRef}
      className="classification-card__inline-dropdown-wrapper"
    >
      <ModalSearchableDropdown
        placeholder={t('classificationItemModal.inputPlaceholder')}
        inputValue={search}
        onInputChange={setSearch}
        suggestedItems={filtered}
        loading={loading}
        onAddClick={handleAddClick}
        isTag={isTag}
        onCreateTag={handleCreateTag}
        compact
      />
    </div>
  );
}

interface TagRowProps {
  type: ItemType;
  label: string;
  tooltipText: string;
  addButtonText: string;
  localItems: string[];
  availableItems: string[];
  isOpen: boolean;
  loading: boolean;
  onToggleOpen: () => void;
  onClose: () => void;
  onAdd: (item: string) => void;
  onRemove: (item: string, type: ItemType) => void;
  isTag?: boolean;
  onCreateTag?: (name: string) => void;
}

const TagRow: React.FC<TagRowProps> = ({
  type,
  label,
  tooltipText,
  addButtonText,
  localItems,
  availableItems,
  isOpen,
  loading,
  onToggleOpen,
  onClose,
  onAdd,
  onRemove,
  isTag,
  onCreateTag,
}) => {
  const { t } = useTranslation();
  const [visibleLimit, setVisibleLimit] = useState(INITIAL_VISIBLE_CHIPS);
  const rowRef = useRef<HTMLDivElement>(null);
  const addButtonRef = useRef<HTMLBdsButtonElement>(null);

  const isFullyExpanded = visibleLimit >= localItems.length;
  const visibleItems = isFullyExpanded ? localItems : localItems.slice(0, visibleLimit);
  const hiddenCount = localItems.length - visibleItems.length;

  const handleToggleLimit = () => {
    if (!isFullyExpanded) {
      return setVisibleLimit((prev) => prev + CHIPS_EXPANSION_STEP);
    }
    setVisibleLimit(INITIAL_VISIBLE_CHIPS);

    setTimeout(() => {
      const targetAnchor = rowRef.current?.closest('.classification-card') ?? rowRef.current;
      targetAnchor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  useEffect(() => {
    const btnElement = addButtonRef.current;
    if (!btnElement) return;

    const handleBdsClick = () => onToggleOpen();
    btnElement.addEventListener('bdsClick', handleBdsClick);

    return () => {
      btnElement.removeEventListener('bdsClick', handleBdsClick);
    };
  }, [onToggleOpen]);

  return (
    <div ref={rowRef} className="classification-card__tag-row">
      <span className="classification-card__tag-label">{label}:</span>
      <div className="classification-card__tag-content">
        <div className="classification-card__add-column" data-classification-add-toggle>
          <BdsTooltip position="top-center" tooltip-text={tooltipText}>
            <BdsButton
              ref={addButtonRef}
              variant="solid"
              color="content"
              icon="plus"
              size="short"
            />
          </BdsTooltip>
          {isOpen && (
            <InlineDropdown
              availableItems={availableItems}
              onAdd={onAdd}
              onClose={onClose}
              loading={loading}
              isTag={isTag}
              onCreateTag={onCreateTag}
            />
          )}
        </div>

        {localItems.length > 0 ? (
          <div className="classification-card__chip-column">
            <ChipGroup
              items={visibleItems}
              onChipClose={(item) => onRemove(item, type)}
            />
          </div>
        ) : (
          <button type="button" className="classification-card__add-btn" onClick={onToggleOpen}>
            {addButtonText}
          </button>
        )}
      </div>

      {localItems.length > INITIAL_VISIBLE_CHIPS && (
        <button
          type="button"
          className="classification-card__show-more-custom"
          onClick={handleToggleLimit}
        >
          <bds-icon
            name={isFullyExpanded ? 'less' : 'plus'}
            size="xx-large"
            theme="outline"
          />
          {isFullyExpanded
            ? t('classificationCard.showLess')
            : t('classificationCard.showMore', { count: hiddenCount })}
        </button>
      )}
    </div>
  );
};

export const ClassificationItemsSection: React.FC<ClassificationItemsSectionProps> = ({
  post,
  onDispatch,
  CLASSIFICATION_EVENTS,
  availableGroups,
  availableThemes,
  availableInterests,
  availableTags,
  loadingAvailableItems,
  onAddTagsLocally,
  onRefreshTags,
}) => {
  const { t } = useTranslation();
  const [inlineDropdown, setInlineDropdown] = useState<ItemType | null>(null);
  const [pendingCreateItem, setPendingCreateItem] = useState<string | null>(null);
  const [customTags, setCustomTags] = useState<string[]>([]);
  const localAvailableTags = useMemo(
    () => [...availableTags, ...customTags.filter((c) => !availableTags.includes(c))],
    [availableTags, customTags],
  );

  const [localNames, setLocalNames] = useState<string[]>(Array.from(new Set(post.names ?? [])));
  const [localThemes, setLocalThemes] = useState<string[]>(Array.from(new Set(post.themes ?? [])));
  const [localOccupations, setLocalOccupations] = useState<string[]>(Array.from(new Set(post.occupations ?? [])));
  const [localTags, setLocalTags] = useState<string[]>(Array.from(new Set(post.tags ?? [])));

  useEffect(() => {
    const handleBulkApply = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      if (detail.postId !== post.pid) return;
      if (detail.groups) setLocalNames((prev) => applySelectionMap(prev, detail.groups));
      if (detail.themes) setLocalThemes((prev) => applySelectionMap(prev, detail.themes));
      if (detail.interests) setLocalOccupations((prev) => applySelectionMap(prev, detail.interests));
      if (detail.tags) setLocalTags((prev) => applySelectionMap(prev, detail.tags));
    };

    document.addEventListener(CLASSIFICATION_EVENTS.BULK_APPLY_LOCAL, handleBulkApply);
    return () => document.removeEventListener(CLASSIFICATION_EVENTS.BULK_APPLY_LOCAL, handleBulkApply);
  }, [post.pid, CLASSIFICATION_EVENTS.BULK_APPLY_LOCAL]);

  const getItemsByType = (type: ItemType): string[] => {
    const map: Record<ItemType, string[]> = {
      group: localNames,
      theme: localThemes,
      interest: localOccupations,
      tag: localTags,
    };
    return map[type];
  };

  const setItemsByType = (type: ItemType, items: string[]) => {
    const map: Record<ItemType, (v: string[]) => void> = {
      group: setLocalNames,
      theme: setLocalThemes,
      interest: setLocalOccupations,
      tag: setLocalTags,
    };
    map[type](items);
  };

  const getAddEventByType = (type: ItemType): [string, string] => {
    const map: Record<ItemType, [string, string]> = {
      group: [CLASSIFICATION_EVENTS.ADD_GROUP, 'group'],
      theme: [CLASSIFICATION_EVENTS.ADD_THEME, 'theme'],
      interest: [CLASSIFICATION_EVENTS.ADD_INTEREST, 'interest'],
      tag: [CLASSIFICATION_EVENTS.ADD_TAG, 'tag'],
    };
    return map[type];
  };

  const applyItemAddition = (item: string, type: ItemType) => {
    const current = getItemsByType(type);
    if (current.includes(item)) return;

    setItemsByType(type, [...current, item]);
    const [eventName, payloadKey] = getAddEventByType(type);
    onDispatch(eventName, { [payloadKey]: item });

    if (type !== 'tag') return;

    const avail = localAvailableTags;
    if (avail.includes(item)) return;

    setCustomTags((prev) => (prev.includes(item) ? prev : [...prev, item]));
    onAddTagsLocally?.([item]);
    selectorService
      .addTags([...avail, item])
      .then(() => onRefreshTags?.())
      .catch((err: unknown) => {
        console.error('[ClassificationItemsSection] Failed to add tags:', err);
      });
  };

  const handleConfirmCreate = () => {
    if (!pendingCreateItem) return;
    applyItemAddition(pendingCreateItem, 'tag');
    setPendingCreateItem(null);
  };

  const removeItemDirectly = (item: string, type: ItemType) => {
    const current = getItemsByType(type);
    setItemsByType(type, current.filter((n) => n !== item));
    const [eventName, payloadKey] = getAddEventByType(type);
    onDispatch(eventName, { [payloadKey]: item, action: 'remove' });
  };

  return (
    <>
      <div className="classification-card__tag-section">
        <div className="classification-card__tag-rows">
          <TagRow
            type="group"
            label={t('classificationCard.groups')}
            tooltipText={t('classificationCard.tooltipAddGroup')}
            addButtonText={t('classificationCard.addNewGroup')}
            localItems={localNames}
            availableItems={availableGroups}
            isOpen={inlineDropdown === 'group'}
            loading={loadingAvailableItems}
            onToggleOpen={() => setInlineDropdown((prev) => (prev === 'group' ? null : 'group'))}
            onClose={() => setInlineDropdown(null)}
            onAdd={(item) => applyItemAddition(item, 'group')}
            onRemove={removeItemDirectly}
          />

          <TagRow
            type="theme"
            label={t('classificationCard.themes')}
            tooltipText={t('classificationCard.tooltipAddTheme')}
            addButtonText={t('classificationCard.addNewTheme')}
            localItems={localThemes}
            availableItems={availableThemes}
            isOpen={inlineDropdown === 'theme'}
            loading={loadingAvailableItems}
            onToggleOpen={() => setInlineDropdown((prev) => (prev === 'theme' ? null : 'theme'))}
            onClose={() => setInlineDropdown(null)}
            onAdd={(item) => applyItemAddition(item, 'theme')}
            onRemove={removeItemDirectly}
          />
        </div>

        <div className="classification-card__tag-rows">
          <TagRow
            type="interest"
            label={t('classificationCard.interests')}
            tooltipText={t('classificationCard.tooltipAddInterest')}
            addButtonText={t('classificationCard.addNewInterest')}
            localItems={localOccupations}
            availableItems={availableInterests}
            isOpen={inlineDropdown === 'interest'}
            loading={loadingAvailableItems}
            onToggleOpen={() => setInlineDropdown((prev) => (prev === 'interest' ? null : 'interest'))}
            onClose={() => setInlineDropdown(null)}
            onAdd={(item) => applyItemAddition(item, 'interest')}
            onRemove={removeItemDirectly}
          />

          <TagRow
            type="tag"
            label={t('classificationCard.tags')}
            tooltipText={t('classificationCard.tooltipAddTag')}
            addButtonText={t('classificationCard.addNewTag')}
            localItems={localTags}
            availableItems={localAvailableTags}
            isOpen={inlineDropdown === 'tag'}
            loading={loadingAvailableItems}
            onToggleOpen={() => setInlineDropdown((prev) => (prev === 'tag' ? null : 'tag'))}
            onClose={() => setInlineDropdown(null)}
            onAdd={(item) => applyItemAddition(item, 'tag')}
            onRemove={removeItemDirectly}
            isTag
            onCreateTag={(name) => { setPendingCreateItem(name); }}
          />
        </div>
      </div>

      {pendingCreateItem && (
        <ClassificationConfirmDialog
          titleKey="classificationItemModal.confirmCreateTitle"
          messageKey="classificationItemModal.confirmCreateMessage"
          messageParams={{ item: pendingCreateItem }}
          confirmKey="classificationItemModal.confirmCreateYes"
          cancelKey="classificationItemModal.confirmCreateNo"
          titleId="ci-create-title"
          onConfirm={handleConfirmCreate}
          onCancel={() => setPendingCreateItem(null)}
        />
      )}
    </>
  );
};

